import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useApi } from "../../../hooks/useApi";
import api from "../../../utils/api";
import "./Categories.scss";

const CategoryNew = () => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    parentId: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Fetch categories for parent selection
  const { data: categoriesData } = useApi(() => api.getAdminCategories(), []);
  const categories = categoriesData?.categories || [];
  const parentCategories = categories.filter(cat => !cat.parentId);

  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !formData.slug) {
      const slug = formData.name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitError("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Category name is required";
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const categoryData = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || null,
        parentId: formData.parentId ? parseInt(formData.parentId) : null,
      };

      await api.createCategory(categoryData);
      history.push("/admin/categories");
    } catch (error) {
      console.error("Error creating category:", error);
      setSubmitError(error.message || "Failed to create category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-categories">
      <div className="categories-header">
        <h2>Add New Category</h2>
        <Link to="/admin/categories" className="btn-secondary">
          Back to Categories
        </Link>
      </div>

      <div className="product-form-container">
        <form onSubmit={handleSubmit} className="product-form">
          {submitError && (
            <div className="form-error-message">{submitError}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">
                Category Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "error" : ""}
                placeholder="Enter category name"
                required
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="slug">
                Slug <span className="required">*</span>
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={errors.slug ? "error" : ""}
                placeholder="category-slug"
                required
              />
              {errors.slug && <span className="field-error">{errors.slug}</span>}
              <small className="form-hint">Auto-generated from name, but you can edit it</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="parentId">Parent Category</label>
            <select
              id="parentId"
              name="parentId"
              value={formData.parentId}
              onChange={handleChange}
            >
              <option value="">None (Top-level category)</option>
              {parentCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <small className="form-hint">Select a parent category to make this a subcategory</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              placeholder="Enter category description"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Creating..." : "Create Category"}
            </button>
            <Link to="/admin/categories" className="btn-secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryNew;

